import {appScreenPath} from '../utils/screenPath';
import React, { useContext, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconChevronRight, IconMenu2 } from '@tabler/icons-react';
import { useFilters, useSidebar } from '../contexts';
import { AppContext } from '../contexts/AppContext';
import { breadcrumbStack } from '../utils/breadcrumbStack';
import './Breadcrumb.css';

interface BreadcrumbProps {
    currentLabel: string;
    appId: string;
    /**
     * Row title shown as subtitle when the breadcrumb stack has none — direct
     * deep-links skip the drill-down push, so the page derives the title from
     * its own data and passes it here.
     */
    subtitleFallback?: string | null;
}

export const Breadcrumb: React.FC<BreadcrumbProps> = ({ currentLabel, appId, subtitleFallback }) => {
    const navigate = useNavigate();
    const { setFilter } = useFilters();
    const { selectedApplication } = useContext(AppContext);
    const { toggleSidebar } = useSidebar();

    // Subscribe so the trail re-renders whenever the stack changes (heal/clear
    // run in navigation effects after this component has already rendered).
    const stack = useSyncExternalStore(breadcrumbStack.subscribe, breadcrumbStack.get);

    const handleGoTo = (index: number) => {
        const entry = breadcrumbStack.truncateTo(index);
        if (!entry?.path) return;

        if (entry.filters) {
            Object.entries(entry.filters).forEach(([key, value]) => {
                setFilter(key, value);
            });
        }

        breadcrumbStack.markDrilldown();
        navigate(entry.path);
    };

    const handleAppClick = () => {
        breadcrumbStack.clear();
        if (selectedApplication?.defaultScreenId) {
            navigate(appScreenPath(selectedApplication, selectedApplication.defaultScreenId));
        }
    };

    // Trailing label-only entry (názov riadku, cez ktorý sa drillovalo) sa
    // nezobrazí ako samostatná omrvinka — zlúči sa s aktuálnou stránkou ako
    // jej subtitle: „Komentár" / „Možnosť presmerovať banner…".
    const lastEntry = stack[stack.length - 1];
    const stackSubtitle = lastEntry && !lastEntry.path ? lastEntry.label : null;
    const subtitle = stackSubtitle ?? subtitleFallback ?? null;
    const visibleStack = stackSubtitle ? stack.slice(0, -1) : stack;

    const items = [
        {
            label: selectedApplication?.project?.displayName || 'Projekt',
            onClick: undefined as (() => void) | undefined
        },
        {
            label: selectedApplication?.displayName || 'Aplikácia',
            onClick: selectedApplication?.defaultScreenId ? handleAppClick : undefined
        },
        ...visibleStack.map((entry, index) => ({
            label: entry.label,
            onClick: entry.path ? () => handleGoTo(index) : undefined
        })),
        {
            label: currentLabel,
            onClick: undefined as (() => void) | undefined
        }
    ];

    const filtered = items.filter((item, i) => {
        if (i === items.length - 1) return true;
        return item.label !== items[i + 1]?.label;
    });

    return (
        <div className="v5-topbar">
            <button
                type="button"
                className="v5-topbar-toggle"
                onClick={toggleSidebar}
                aria-label="Prepnúť menu"
            >
                <IconMenu2 size={18} />
            </button>
            <div className="v5-crumbs">
                {filtered.map((item, index) => {
                    const isLast = index === filtered.length - 1;
                    const isClickable = !isLast && !!item.onClick;

                    return (
                        <React.Fragment key={index}>
                            {index > 0 && (
                                <IconChevronRight size={11} className="v5-crumb-sep" />
                            )}
                            {isClickable ? (
                                <button
                                    type="button"
                                    className="v5-crumb"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        item.onClick!();
                                    }}
                                >
                                    {item.label}
                                </button>
                            ) : isLast && subtitle ? (
                                <span className="v5-crumb-cur-wrap">
                                    <span className="v5-crumb-sub" title={subtitle}>
                                        {subtitle}
                                    </span>
                                    <span className="v5-crumb-cur">{item.label}</span>
                                </span>
                            ) : (
                                <span className={isLast ? 'v5-crumb-cur' : 'v5-crumb'}>
                                    {item.label}
                                </span>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};
